import { Preferences } from "@capacitor/preferences";

import { isNativeMobileApp } from "@/utils/platform.helpers";

const MIGRATION_COMPLETE_KEY = "storage_migration_v1_complete";

/**
 * All localStorage keys that need to be migrated to Preferences.
 * This list should be kept up to date when new storage keys are added.
 */
const KEYS_TO_MIGRATE = [
  // Core app state
  "baseApiUrl",
  "platformId",
  "iconNames",
  "referralCode",
  "scannedCode",

  // Navigation history
  "routeBackNumber",
  "unlockedShopItems",
  "currentLocation",

  // Gift card flow
  "isGiftCardClaim",
  "giftCardTransferId",
  "giftCardRecipientPhone",
  "giftCardRecipientPlatformUserId",
  "giftCardImageUrl",
  "giftCardAmount",
  "giftCardInfo",

  // Referral flow
  "statelessReferralCode",
  "referralSenderName",

  // Onboarding
  "onboarding_context_state",
  "locationConfirmed",

  // Adonis chat
  "adonisChatHistory",

  // Auth token (handled by AuthService but include for completeness)
  "repeatmd-app-auth-token",
];

/**
 * Migrates localStorage data to Capacitor Preferences.
 *
 * This function should be called ONCE when the app starts.
 * It only runs on native platforms (iOS/Android) since Capacitor Preferences
 * already uses localStorage on web.
 *
 * IMPORTANT: This function is designed to NEVER throw or block app startup.
 * Any errors are caught and logged, allowing the app to continue normally.
 *
 * Migration logic:
 * 1. Skip if not on native platform
 * 2. Skip if migration already complete
 * 3. For each known key: if exists in localStorage but NOT in Preferences, copy it
 * 4. Mark migration complete
 */
export async function migrateLocalStorageToPreferences(): Promise<void> {
  try {
    // Only run on native platforms - Preferences uses localStorage on web anyway
    if (!isNativeMobileApp()) {
      return;
    }

    // Check if migration already completed
    const { value: migrationComplete } = await Preferences.get({
      key: MIGRATION_COMPLETE_KEY,
    });

    if (migrationComplete === "true") {
      return;
    }

    console.log("[Storage Migration] Starting migration from localStorage to Preferences...");

    let migratedCount = 0;

    for (const key of KEYS_TO_MIGRATE) {
      try {
        // Check if key exists in localStorage
        const localValue = localStorage.getItem(key);
        if (!localValue) {
          continue;
        }

        // Check if key already exists in Preferences
        const { value: preferencesValue } = await Preferences.get({ key });
        if (preferencesValue) {
          // Already in Preferences, skip (don't overwrite)
          continue;
        }

        // Migrate: copy from localStorage to Preferences
        await Preferences.set({ key, value: localValue });
        migratedCount++;

        console.log(`[Storage Migration] Migrated key: ${key}`);
      } catch (error) {
        // Log but don't throw - continue with other keys
        console.error(`[Storage Migration] Failed to migrate key: ${key}`, error);
      }
    }

    // Mark migration as complete
    await Preferences.set({ key: MIGRATION_COMPLETE_KEY, value: "true" });

    console.log(`[Storage Migration] Complete. Migrated ${migratedCount} keys.`);
  } catch (error) {
    // Catch any unexpected errors - NEVER block app startup
    console.error("[Storage Migration] Migration failed, skipping:", error);
  }
}

/**
 * Migrates dynamic keys that include a userId suffix.
 * Call this after user authentication is complete.
 *
 * IMPORTANT: This function is designed to NEVER throw.
 */
export async function migrateUserSpecificKeys(userId: string): Promise<void> {
  try {
    if (!isNativeMobileApp() || !userId) {
      return;
    }

    const dynamicKeys = [
      `repeatmd-app-last-idle-time-in-timestamp-${userId}`,
      `promotionModalSeen_${userId}`,
    ];

    for (const key of dynamicKeys) {
      try {
        const localValue = localStorage.getItem(key);
        if (!localValue) continue;

        const { value: preferencesValue } = await Preferences.get({ key });
        if (preferencesValue) continue;

        await Preferences.set({ key, value: localValue });
        console.log(`[Storage Migration] Migrated user key: ${key}`);
      } catch (error) {
        console.error(`[Storage Migration] Failed to migrate user key: ${key}`, error);
      }
    }
  } catch (error) {
    console.error("[Storage Migration] User migration failed, skipping:", error);
  }
}
